// src/calendar/CalendarPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  startOfWeekSunday,
  fmtYMD,
  fmtFriendly,
  getRange,
  addDays,
} from "./calendarUtils";
import CalendarDayView from "./CalendarDayView";
import CalendarWeekView from "./CalendarWeekView";
import CalendarMonthView from "./CalendarMonthView";
import AppointmentModal from "./AppointmentModal";
import AppointmentPrintSheet from "./AppointmentPrintSheet";
import RecurringAppointmentModal from "./RecurringAppointmentModal";
import CalendarSecondaryToolbar from "./CalendarSecondaryToolbar";
import RecurringSeriesSchedule from "./RecurringSeriesSchedule";
import CalendarPrintDialog from "./CalendarPrintDialog";
import { deleteRecurringFuture } from "./recurringAppointmentApi";
import SavedToastOverlay from "../components/SavedToastOverlay";

import {
  fetchCustomers,
  fetchEmployees,
  fetchHours,
  fetchJobTypes,
  fetchBusinessSettings,
  fetchAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointmentApi,
  createCustomer,
} from "./calendarApi";

const recurringAppointmentsEnabled =
  process.env.REACT_APP_APPOINTMENT_RECURRENCE_ENABLED === "true";

const CalendarPage = (props) => {
  // =========================
  // Responsive
  // =========================
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // =========================
  // Navigation (matches your app pattern)
  // =========================
  const goToDashboard = () => {
    if (typeof props?.navigate === "function") return props.navigate("dashboard");
    if (typeof props?.setActiveModule === "function") return props.setActiveModule("dashboard");
    if (typeof props?.setActivePage === "function") return props.setActivePage("dashboard");
    if (typeof props?.setView === "function") return props.setView("dashboard");
    if (typeof props?.onBackToDashboard === "function") return props.onBackToDashboard();
    if (typeof props?.goToDashboard === "function") return props.goToDashboard();
    if (typeof window.setActiveModule === "function") return window.setActiveModule("dashboard");
    if (typeof window.setActivePage === "function") return window.setActivePage("dashboard");
    if (typeof window.setView === "function") return window.setView("dashboard");
    return (window.location.href = "/dashboard");
  };

  // =========================
  // View Mode
  // =========================
  const VIEW = useMemo(() => ({ DAY: "day", WEEK: "week", MONTH: "month" }), []);
  const [view, setView] = useState(VIEW.WEEK);
  const [anchorDate, setAnchorDate] = useState(new Date());

  // =========================
  // Data (DB/API truth)
  // =========================
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [businessName, setBusinessName] = useState("");
  const [bizHours, setBizHours] = useState({
    openTime: "08:00",
    closeTime: "17:00",
    closedDays: [0, 6],
    bufferPercentage: 0,
  });
  const [appointments, setAppointments] = useState([]);
  const [banner, setBanner] = useState("");

  // =========================
  // Modal / Draft
  // =========================
  const [showModal, setShowModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showRecurringSeries, setShowRecurringSeries] = useState(false);
  const [showCalendarPrint, setShowCalendarPrint] = useState(false);
  const [showPrintSheet, setShowPrintSheet] = useState(false);
  const [useCustomerAddress, setUseCustomerAddress] = useState(false);
  const [recurringDeletionPending, setRecurringDeletionPending] = useState(false);
  const recurringDeletionInFlightRef = useRef(false);

  const closeAppointmentModal = () => {
    if (recurringDeletionInFlightRef.current) return;
    setShowModal(false);
  };

  const blankDraft = (d = new Date()) => ({
    id: null,
    date: fmtYMD(d),
    start_time: "09:00",
    end_time: "10:00",
    end_date: "",
    customer_id: "",
    employee_id: "",
    job_type_id: "",
    title: "",
    estimate_on_file: false,
    service_street: "",
    service_city: "",
    service_state: "",
    service_zip: "",
    details: "",
    customer_name: "",
    employee_name: "",
    employee_color: "",
    job_type_name: "",
    job_type_color: "",
  });

  const [draft, setDraft] = useState(blankDraft(new Date()));
  const [openedAppointment, setOpenedAppointment] = useState(null);
  const setDraftField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // =========================
  // Quick Add Customer
  // =========================
  const [showQuickAddCustomerUI, setShowQuickAddCustomerUI] = useState(false);
  const [quickCustomer, setQuickCustomer] = useState({
    company: "",
    contact: "",
    email: "",
    office_phone: "",
    mobile_phone: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    terms: "COD",
    notes: "",
  });
  const digitsOnly = (s) => String(s || "").replace(/\D+/g, "");
  const formatUSPhone = (raw) => {
    const d = digitsOnly(raw).slice(0, 10);
    if (!d) return "";
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const setQC = (k, v) => {
    let next = v;
    if (k === "office_phone" || k === "mobile_phone") next = formatUSPhone(v);
    setQuickCustomer((c) => ({ ...c, [k]: next }));
  };

  // =========================
  // Helpers
  // =========================
  const todayYMD = useMemo(() => fmtYMD(new Date()), []);
  const monthLabel = useMemo(() => {
    try {
      return anchorDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    } catch {
      return "Month";
    }
  }, [anchorDate]);

  const findCustomer = (id) => (customers || []).find((c) => String(c.id) === String(id));

  const customerLabel = useMemo(() => {
    if (!draft.customer_id) return "";
    const c = findCustomer(draft.customer_id);
    if (!c) return "";
    const name = String(c.name || c.company || "").trim();
    const contact = String(c.contactPerson || "").trim();
    return [name, contact].filter(Boolean).join(" — ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.customer_id, customers]);

  const applyCustomerAddressNow = () => {
    if (!draft.customer_id) return;
    const c = findCustomer(draft.customer_id);
    if (!c) return;
    setDraft((d) => ({
      ...d,
      service_street: String(c.street || "").trim(),
      service_city: String(c.city || "").trim(),
      service_state: String(c.state || "").trim(),
      service_zip: String(c.zip || "").trim(),
    }));
  };

  useEffect(() => {
    if (!useCustomerAddress) return;
    if (!draft.customer_id) return;
    applyCustomerAddressNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCustomerAddress, draft.customer_id]);

  // =========================
  // Loaders
  // =========================
  const normalizeHours = (raw) => {
    const dayMap = [
      { key: "sunday", dow: 0 },
      { key: "monday", dow: 1 },
      { key: "tuesday", dow: 2 },
      { key: "wednesday", dow: 3 },
      { key: "thursday", dow: 4 },
      { key: "friday", dow: 5 },
      { key: "saturday", dow: 6 },
    ];
    const closedDays = [];
    let openTime = "08:00";
    let closeTime = "17:00";
    let foundAnyOpen = false;
    for (const d of dayMap) {
      const val = String(raw?.[d.key] || "").trim();
      if (!val || val.toLowerCase() === "closed") {
        closedDays.push(d.dow);
        continue;
      }
      const parts = val.split("-").map((s) => s.trim());
      if (parts.length === 2 && parts[0] && parts[1] && !foundAnyOpen) {
        openTime = parts[0];
        closeTime = parts[1];
        foundAnyOpen = true;
      }
    }
    return { openTime, closeTime, closedDays, bufferPercentage: Number(raw?.bufferPercentage || 0) };
  };

  const loadCustomers = async () => {
    const rows = await fetchCustomers();
    setCustomers(Array.isArray(rows) ? rows : []);
  };

  const loadEmployees = async () => {
    const rows = await fetchEmployees();
    setEmployees(Array.isArray(rows) ? rows : []);
  };

  const loadHours = async () => {
    const h = await fetchHours();
    setBizHours(normalizeHours(h || {}));
  };

  const loadJobTypes = async () => {
    const rows = await fetchJobTypes();
    setJobTypes(Array.isArray(rows) ? rows : []);
  };

  const loadBusinessSettings = async () => {
    const settings = await fetchBusinessSettings();
    setBusinessName(String(settings?.businessName || "").trim());
  };

  const loadAppointmentsForRange = async (baseDate, mode) => {
    const range = getRange(baseDate, mode, VIEW);
    const rows = await fetchAppointments({ start: range.start, end: range.end });
    setAppointments((Array.isArray(rows) ? rows : []).map(r => ({ ...r, date: (r.start_time ? String(r.start_time).slice(0,10) : "") })));
  };

  const refreshAll = async () => {
    setBanner("");
    try {
      await Promise.allSettled([
        loadCustomers(),
        loadEmployees(),
        loadHours(),
        loadJobTypes(),
        loadBusinessSettings(),
      ]);
      await loadAppointmentsForRange(anchorDate, view);
    } catch {
      setBanner("⚠️ Refresh failed (network/server).");
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAppointmentsForRange(anchorDate, view).catch(() => setAppointments([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorDate, view]);

  // =========================
  // Derived ranges + grouping
  // =========================
  const weekStart = useMemo(() => startOfWeekSunday(anchorDate), [anchorDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const monthFirst = useMemo(() => new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1), [anchorDate]);
  const monthGridStart = useMemo(() => startOfWeekSunday(monthFirst), [monthFirst]);
  const monthGridDays = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(monthGridStart, i)), [monthGridStart]);

  const apptsByDate = useMemo(() => {
    const map = {};
    for (const a of appointments || []) {
      const startDate = String(a?.date || a?.start_time || "").slice(0, 10).trim();
      if (!startDate) continue;
      const rawEndDate = String(a?.end_date || a?.end_time || "").slice(0, 10).trim();
      const endDate = rawEndDate > startDate ? rawEndDate : startDate;
      const isMultiDay = endDate > startDate;
      let cursor = startDate;
      for (;;) {
        if (!map[cursor]) map[cursor] = [];
        map[cursor].push({
          ...a,
          _startDate: startDate,
          _endDate: endDate,
          isMultiDay,
          isSpanStart: cursor === startDate,
          isSpanEnd: cursor === endDate,
          isSpanMiddle: isMultiDay && cursor !== startDate && cursor !== endDate,
        });
        if (cursor === endDate) break;
        const nxt = new Date(cursor + "T00:00:00");
        nxt.setDate(nxt.getDate() + 1);
        cursor = fmtYMD(nxt);
      }
    }
    for (const k of Object.keys(map)) {
      map[k].sort((x, y) => String(x.start_time || "").localeCompare(String(y.start_time || "")));
    }
    return map;
  }, [appointments]);

  // =========================
  // Open modal
  // =========================
  const openNewForDate = (dateObj) => {
    const d = dateObj || new Date();
    setBanner("");
    setShowQuickAddCustomerUI(false);
    setUseCustomerAddress(false);
    setOpenedAppointment(null);
    setDraft(blankDraft(d));
    setShowModal(true);
  };

  const openDayForDate = (dateObj) => {
    setAnchorDate(dateObj);
    setView(VIEW.DAY);
  };

  const openEdit = (appt) => {
    setBanner("");
    setShowQuickAddCustomerUI(false);

    // Safely extract HH:MM from datetime string (handles "2026-03-05 13:00:00" or "2026-03-05T13:00:00")
    const extractTime = (dt) => {
      if (!dt) return "09:00";
      const s = String(dt).replace("T", " ");
      const parts = s.split(" ");
      if (parts.length >= 2) return parts[1].slice(0, 5);
      return s.slice(0, 5) || "09:00";
    };

    const trueStartDate = String(appt?._startDate || appt?.date || fmtYMD(new Date()));
    const rawEndDate = String(appt?._endDate || appt?.end_time || appt?.end_datetime || "").slice(0, 10);
    const endDateVal = rawEndDate && rawEndDate !== trueStartDate ? rawEndDate : "";

    const next = {
      id: appt?.id ?? null,
      recurrence_batch_id: appt?.recurrence_batch_id,
      recurrence_sequence: appt?.recurrence_sequence,
      date: trueStartDate,
      start_time: appt?.start_hhmm || extractTime(appt?.start_time),
      end_time: appt?.end_hhmm || extractTime(appt?.end_time),
      end_date: endDateVal,
      customer_id: appt?.customer_id ? String(appt.customer_id) : "",
      employee_id: appt?.employee_id ? String(appt.employee_id) : "",
      job_type_id: appt?.job_type_id ? String(appt.job_type_id) : (appt?.appointment_type_id ? String(appt.appointment_type_id) : ""),
      title: String(appt?.title || "").trim(),
      estimate_on_file: !!appt?.estimate_on_file,
      service_street: String(appt?.service_street || "").trim(),
      service_city: String(appt?.service_city || "").trim(),
      service_state: String(appt?.service_state || "").trim(),
      service_zip: String(appt?.service_zip || "").trim(),
      details: String(appt?.details || "").trim(),
      notes: String(appt?.notes || "").trim(),
      customer_name: String(appt?.customer_name || "").trim(),
      employee_name: String(appt?.employee_name || "").trim(),
      employee_color: String(appt?.employee_color || "").trim(),
      job_type_name: String(appt?.job_type_name || "").trim(),
      job_type_color: String(appt?.job_type_color || "").trim(),
    };
    setDraft(next);
    const cid = next.customer_id;
    if (cid) {
      const c = findCustomer(cid);
      const match = c &&
        String(c.street || "").trim() === String(next.service_street || "").trim() &&
        String(c.city || "").trim() === String(next.service_city || "").trim() &&
        String(c.state || "").trim() === String(next.service_state || "").trim() &&
        String(c.zip || "").trim() === String(next.service_zip || "").trim();
      setUseCustomerAddress(!!match);
    } else {
      setUseCustomerAddress(false);
    }
    setOpenedAppointment(appt);
    setShowModal(true);
  };

  // =========================
  // Save appointment
  // =========================
  const saveAppointment = async () => {
    if (recurringDeletionInFlightRef.current) return;
    try {
      const emp = (employees || []).find((e) => String(e.id) === String(draft.employee_id));
      const jt = (jobTypes || []).find((j) => String(j.id) === String(draft.job_type_id));
      const effectiveEndDate = draft.end_date || draft.date;
      const payload = {
        id: draft.id || undefined,
        date: draft.date,
        end_date: effectiveEndDate,
        title: String(draft.title || draft.customer_name || "Appointment").trim(),
        start_time: `${draft.date} ${draft.start_time}:00`,
        end_time: `${effectiveEndDate} ${draft.end_time}:00`,
        start_datetime: `${draft.date} ${draft.start_time}:00`,
        end_datetime: `${effectiveEndDate} ${draft.end_time}:00`,
        customer_id: draft.customer_id ? Number(draft.customer_id) : null,
        employee_id: draft.employee_id ? Number(draft.employee_id) : null,
        appointment_type_id: draft.job_type_id ? Number(draft.job_type_id) : null,
        job_type_id: draft.job_type_id ? Number(draft.job_type_id) : null,
        customer_name: customerLabel,
        employee_name: String(emp?.name || emp?.full_name || "").trim(),
        employee_color: emp?.color || "",
        job_type_name: String(jt?.name || jt?.title || "").trim(),
        job_type_color: jt?.color || "",
        estimate_on_file: !!draft.estimate_on_file,
        service_street: String(draft.service_street || "").trim(),
        service_city: String(draft.service_city || "").trim(),
        service_state: String(draft.service_state || "").trim(),
        service_zip: String(draft.service_zip || "").trim(),
        details: String(draft.details || "").trim(),
      };
      let createdId = null;
      if (payload.id) {
        await updateAppointment(payload.id, payload);
      } else {
        const created = await createAppointment(payload);
        createdId = Number(created?.id);
        if (!Number.isSafeInteger(createdId) || createdId < 1) {
          throw new Error("Appointment was created, but its ID was not returned.");
        }
      }
      await loadAppointmentsForRange(anchorDate, view);
      if (createdId) setDraft((current) => ({ ...current, id: createdId }));
      window.dispatchEvent(new CustomEvent("jobflow:saved", {
        detail: { message: payload.id ? "APPOINTMENT UPDATED!" : "APPOINTMENT CREATED!" },
      }));
      if (payload.id) setShowModal(false);
      setBanner("");
    } catch (err) {
      console.error("APPT_SAVE_FAILED", err);
      setBanner(`⚠️ Appointment save failed: ${String(err?.message || err)}`);
    }
  };

  const deleteAppointment = async () => {
    if (!draft?.id) return;
    try {
      await deleteAppointmentApi(draft.id);
    } catch {
      setBanner("⚠️ Delete failed (network/server).");
      return;
    }
    setShowModal(false);
    setBanner("");
    try {
      await loadAppointmentsForRange(anchorDate, view);
    } catch {
      setBanner("Appointment deleted, but Calendar refresh failed. Select Refresh to reload.");
    }
  };

  const runRecurringDeletion = async (deleteRequest) => {
    if (!draft?.id || recurringDeletionInFlightRef.current) return;
    recurringDeletionInFlightRef.current = true;
    setRecurringDeletionPending(true);
    try {
      try {
        await deleteRequest(draft.id);
      } catch {
        setBanner("⚠️ Delete failed (network/server).");
        return;
      }
      setShowModal(false);
      setBanner("");
      try {
        await loadAppointmentsForRange(anchorDate, view);
      } catch {
        setBanner("Appointment deleted, but Calendar refresh failed. Select Refresh to reload.");
      }
    } finally {
      recurringDeletionInFlightRef.current = false;
      setRecurringDeletionPending(false);
    }
  };

  const deleteRecurringAppointment = () => runRecurringDeletion(deleteAppointmentApi);
  const deleteRecurringAppointments = () => runRecurringDeletion(deleteRecurringFuture);

  // =========================
  // Save Quick Add Customer
  // =========================
  const saveQuickCustomer = async () => {
    try {
      const payload = {
        company: String(quickCustomer.company || "").trim(),
        name: String(quickCustomer.company || quickCustomer.contact || "").trim(),
        contactPerson: String(quickCustomer.contact || "").trim(),
        billingEmail: String(quickCustomer.email || "").trim(),
        phone: String(quickCustomer.office_phone || "").trim(),
        mobile_phone: String(quickCustomer.mobile_phone || "").trim(),
        street: String(quickCustomer.street || "").trim(),
        city: String(quickCustomer.city || "").trim(),
        state: String(quickCustomer.state || "").trim(),
        zip: String(quickCustomer.zip || "").trim(),
        terms: String(quickCustomer.terms || "COD").trim(),
        notes: String(quickCustomer.notes || "").trim(),
      };
      const created = await createCustomer(payload);
      const newId = created?.id || created?.customer_id;
      if (newId) {
        const createdCustomer = {
          id: newId,
          name: payload.name,
          company: payload.company || payload.name,
          contactPerson: payload.contactPerson,
          billingEmail: payload.billingEmail,
          phone: payload.phone,
          office_phone: payload.phone,
          mobile_phone: payload.mobile_phone,
          street: payload.street,
          city: payload.city,
          state: payload.state,
          zip: payload.zip,
          terms: payload.terms,
          notes: payload.notes,
        };
        setCustomers((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          const exists = arr.some((c) => String(c?.id) === String(newId));
          return exists ? arr : [createdCustomer, ...arr];
        });
        setDraft((d) => ({ ...d, customer_id: String(newId) }));
      }
      const refreshed = await loadCustomers();
      if (!newId) {
        const wantName = String(payload.name || "").trim().toLowerCase();
        const wantEmail = String(payload.billingEmail || "").trim().toLowerCase();
        const wantPhone = String(payload.phone || "").replace(/\D+/g, "").slice(-10);
        const wantMobile = String(payload.mobile_phone || "").replace(/\D+/g, "").slice(-10);
        const match = (Array.isArray(refreshed) ? refreshed : []).find((c) => {
          const name = String(c?.name || c?.company || "").trim().toLowerCase();
          const email = String(c?.billingEmail || c?.email || "").trim().toLowerCase();
          const phone = String(c?.phone || c?.office_phone || "").replace(/\D+/g, "").slice(-10);
          const mobile = String(c?.mobile_phone || "").replace(/\D+/g, "").slice(-10);
          if (wantEmail && email && email === wantEmail) return true;
          if (wantPhone && phone && phone === wantPhone) return true;
          if (wantMobile && mobile && mobile === wantMobile) return true;
          if (wantName && name && name === wantName) return true;
          return false;
        });
        const foundId = match?.id;
        if (foundId) setDraft((d) => ({ ...d, customer_id: String(foundId) }));
      }
      if (useCustomerAddress) applyCustomerAddressNow();
      window.dispatchEvent(new CustomEvent("jobflow:saved", {
        detail: { message: "CUSTOMER ADDED!" },
      }));
      setShowQuickAddCustomerUI(false);
      setBanner("");
    } catch (err) {
      console.error("QC_SAVE_FAILED", err);
      setBanner(`⚠️ Customer save failed: ${String(err?.message || err)}`);
    }
  };

  // =========================
  // Derived data for print sheet
  // =========================
  const currentCustomer = useMemo(() => (customers || []).find((c) => String(c.id) === String(draft.customer_id)), [draft.customer_id, customers]);
  const currentEmployee = useMemo(() => (employees || []).find((e) => String(e.id) === String(draft.employee_id)), [draft.employee_id, employees]);
  const currentJobType = useMemo(() => (jobTypes || []).find((j) => String(j.id) === String(draft.job_type_id)), [draft.job_type_id, jobTypes]);

  const onMyWayLinks = useMemo(() => {
    if (!draft.id || !currentCustomer) return { text: "", email: "" };

    const rawMobile = String(currentCustomer.mobile_phone || "").trim();
    const normalizedMobile = rawMobile.startsWith("+")
      ? `+${rawMobile.slice(1).replace(/\D/g, "")}`
      : rawMobile.replace(/\D/g, "");
    const mobileDigitCount = normalizedMobile.replace(/\D/g, "").length;
    const email = String(
      currentCustomer.billingEmail || currentCustomer.email || ""
    ).trim();

    const firstName =
      String(currentCustomer.contactPerson || "")
        .trim()
        .split(/\s+/)
        .find(Boolean) || "";
    const sender = businessName || "your service provider";
    const greeting = firstName ? `Hi ${firstName},` : "Hi,";
    const message = `${greeting} this is ${sender}. We're on our way.`;

    return {
      text:
        mobileDigitCount >= 7 && mobileDigitCount <= 15
          ? `sms:${normalizedMobile}?body=${encodeURIComponent(message)}`
          : "",
      email: email
        ? `mailto:${email}?subject=${encodeURIComponent("On My Way")}&body=${encodeURIComponent(message)}`
        : "",
    };
  }, [businessName, currentCustomer, draft.id]);

  const directionsHref = useMemo(() => {
    if (!draft.id) return "";
    const persisted = (appointments || []).find((a) => String(a.id) === String(draft.id)) ||
      (String(openedAppointment?.id) === String(draft.id) ? openedAppointment : null);
    if (!persisted) return "";

    const persistedAddress = {
      street: String(persisted.service_street || "").trim(),
      city: String(persisted.service_city || "").trim(),
      state: String(persisted.service_state || "").trim(),
      zip: String(persisted.service_zip || "").trim(),
    };
    const draftAddress = {
      street: String(draft.service_street || "").trim(),
      city: String(draft.service_city || "").trim(),
      state: String(draft.service_state || "").trim(),
      zip: String(draft.service_zip || "").trim(),
    };
    const addressMatches = Object.keys(persistedAddress).every(
      (key) => persistedAddress[key] === draftAddress[key]
    );
    const hasUsableDestination = Boolean(
      persistedAddress.street &&
      (persistedAddress.city || persistedAddress.state || persistedAddress.zip)
    );
    if (!addressMatches || !hasUsableDestination) return "";

    const locality = [persistedAddress.city, persistedAddress.state, persistedAddress.zip]
      .filter(Boolean)
      .join(" ");
    const destination = [persistedAddress.street, locality].filter(Boolean).join(", ");
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  }, [appointments, draft.id, draft.service_city, draft.service_state, draft.service_street, draft.service_zip, openedAppointment]);

  // =========================
  // Styles
  // =========================
  const BTN_H = 44;
  const BTN_MIN_W = 120;

  const styles = {
    pageWrap: {
      padding: isMobile ? 14 : 18,
      maxWidth: 1400,
      margin: "0 auto",
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      overflowX: "hidden",
    },
    titleBarOuter: {
      border: "6px solid #000000",
      background: "#ffffff",
      borderRadius: 12,
      padding: 10,
      marginBottom: 14,
      boxSizing: "border-box",
    },
    titleBarInner: {
      border: "3px solid #E8761D",
      borderRadius: 10,
      background: "white",
      padding: isMobile ? 10 : 12,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      minWidth: 0,
      boxSizing: "border-box",
    },
    titleLeft: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
    titleIconBox: {
      width: 42, height: 42, border: "3px solid #000000", borderRadius: 10,
      display: "grid", placeItems: "center", background: "#e0e7ff", flexShrink: 0,
    },
    titleTextWrap: { display: "flex", flexDirection: "column", gap: 2 },
    titleMain: { fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: isMobile ? 20 : 26, lineHeight: 1.1 },
    titleSub: { fontWeight: 900, opacity: 0.75, fontSize: isMobile ? 11 : 12, textTransform: "uppercase" },
    titleActionBtn: {
      height: BTN_H, minWidth: BTN_MIN_W, padding: "0 14px",
      border: "3px solid #E8761D", borderRadius: 12,
      background: "#000000", color: "#E8C98F",
      fontWeight: 900, cursor: "pointer", userSelect: "none",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      gap: 8, boxSizing: "border-box", whiteSpace: "nowrap",
    },
    btn: (active, calendarControl = false) => ({
      height: BTN_H,
      minWidth: calendarControl ? (isMobile ? 0 : 88) : BTN_MIN_W,
      padding: calendarControl ? (isMobile ? "0 6px" : "0 10px") : "0 14px",
      border: "3px solid #ffffff", borderRadius: 999,
      background: active ? "#E8761D" : "white",
      fontWeight: 900, cursor: "pointer", userSelect: "none",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      gap: 8, boxSizing: "border-box", whiteSpace: "nowrap",
      fontSize: isMobile && calendarControl ? 12 : undefined,
    }),
    controlsRow: {
      display: isMobile ? "flex" : "grid",
      gridTemplateColumns: isMobile ? "1fr" : "auto 1fr auto",
      flexDirection: isMobile ? "column" : undefined,
      gap: isMobile ? 6 : 10, alignItems: "center", marginBottom: 12,
      minWidth: 0,
      width: "100%",
    },
    leftGroup: {
      display: isMobile ? "grid" : "flex",
      gridTemplateColumns: isMobile ? "repeat(3, minmax(0, 1fr))" : undefined,
      gap: isMobile ? 6 : 8,
      flexWrap: "nowrap",
      alignItems: "center",
      width: isMobile ? "100%" : undefined,
      order: isMobile ? 2 : undefined,
    },
    centerGroup: {
      display: "flex", justifyContent: "center", alignItems: "center",
      fontWeight: 900, fontSize: isMobile ? 16 : 18,
      textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap",
      order: isMobile ? 1 : undefined,
      width: isMobile ? "100%" : undefined,
    },
    monthFrame: {
      padding: "6px 14px", border: "3px solid #E8761D", borderRadius: 10,
      background: "#ffffff", color: "#000000", boxSizing: "border-box",
    },
    rightGroup: {
      display: isMobile ? "grid" : "flex",
      gridTemplateColumns: isMobile ? "repeat(3, minmax(0, 1fr))" : undefined,
      gap: isMobile ? 6 : 8,
      flexWrap: "nowrap",
      alignItems: "center",
      justifyContent: isMobile ? undefined : "flex-end",
      width: isMobile ? "100%" : undefined,
      order: isMobile ? 3 : undefined,
    },
    board: {
      border: "6px solid #E8761D", borderRadius: 18, background: "#000000",
      padding: isMobile ? 10 : 14,
      boxSizing: "border-box", minWidth: 0,
    },
    boardInner: {
      border: "3px solid #E8E2DA", borderRadius: 14, background: "#365C91",
      padding: isMobile ? 10 : 14, boxSizing: "border-box", minWidth: 0, color: "white",
    },
    weekScroll: {
      width: "100%", overflowX: "auto", overflowY: "hidden",
      WebkitOverflowScrolling: "touch", paddingBottom: 8, boxSizing: "border-box",
    },
    weekGrid: {
      display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
      gap: 10, width: "100%", minWidth: isMobile ? 980 : "100%", boxSizing: "border-box",
    },
    dayCol: {
      border: "3px solid #E8E2DA", borderRadius: 14, background: "#365C91",
      overflow: "hidden", minWidth: 0, boxSizing: "border-box",
    },
    dayColHeader: (isToday) => ({
      padding: "10px 10px", borderBottom: "4px solid #E8E2DA",
      background: isToday ? "#E8761D" : "#e5e7eb", color: "black",
      fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
    }),
    plusBtn: {
      border: "3px solid #ffffff", borderRadius: 10, background: "white", fontWeight: 900,
      padding: isMobile ? "10px 14px" : "6px 10px", fontSize: isMobile ? 18 : 14,
      minWidth: isMobile ? 44 : 0, minHeight: isMobile ? 44 : 0, lineHeight: 1, cursor: "pointer", userSelect: "none",
    },
    dayColBody: { padding: 6, display: "flex", flexDirection: "column", gap: 4 },
    apptCard: (jtColor, empColor) => ({
      border: "2px solid #E8E2DA", borderRadius: 8,
      background: jtColor ? jtColor : "white", color: "black",
      padding: "3px 4px", cursor: "pointer", position: "relative", boxSizing: "border-box", minWidth: 0,
    }),
    empStripe: (empColor) => ({
      position: "absolute", left: 0, top: 0, bottom: 0, width: 6,
      background: empColor || "#2f3f5a", borderRight: "2px solid black",
      borderTopLeftRadius: 6, borderBottomLeftRadius: 6,
    }),
    apptTitle: { fontWeight: 900, marginLeft: 9, fontSize: 12, lineHeight: 1.05 },
    apptSub: { fontWeight: 800, opacity: 0.85, marginLeft: 9, marginTop: 1, fontSize: 10, lineHeight: 1.05 },
    monthGrid: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 10, width: "100%", boxSizing: "border-box" },
    monthCell: (isToday) => ({
      border: "3px solid #E8E2DA", borderRadius: 14,
      background: isToday ? "#E8761D" : "#24324a", color: isToday ? "black" : "white",
      padding: 10, minHeight: 120, boxSizing: "border-box", overflow: "hidden", minWidth: 0,
    }),
    monthTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 },
    monthNum: { fontWeight: 900 },
    monthList: { display: "flex", flexDirection: "column", gap: 4 },
    subtle: { marginTop: 10, fontWeight: 900, opacity: 0.85 },
    modalOverlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
      zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
      padding: isMobile ? 10 : 18, boxSizing: "border-box",
    },
    modalCard: {
      width: "100%", maxWidth: 980, border: "3px solid #E8E2DA", borderRadius: 18,
      background: "#365C91", color: "white", boxShadow: "0 18px 0 rgba(0,0,0,0.25)",
      maxHeight: "calc(100vh - 40px)", display: "flex", flexDirection: "column",
      overflow: "hidden", boxSizing: "border-box",
    },
    modalBody: { padding: isMobile ? 12 : 16, overflowY: "auto", WebkitOverflowScrolling: "touch", boxSizing: "border-box" },
    section: { border: "3px solid #E8E2DA", borderRadius: 14, background: "#365C91", padding: 12, marginTop: 12, boxSizing: "border-box" },
    sectionTitle: { fontWeight: 900, marginBottom: 10, textTransform: "uppercase" },
    row3: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10 },
    row2: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 },
    row4: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 0.6fr 1fr", gap: 10 },
    input: { width: "100%", padding: "12px 12px", border: "3px solid #ffffff", borderRadius: 12, fontWeight: 900, boxSizing: "border-box" },
    select: { width: "100%", padding: "12px 12px", border: "3px solid #ffffff", borderRadius: 12, fontWeight: 900, boxSizing: "border-box", background: "white" },
    textarea: { width: "100%", minHeight: 120, padding: 12, border: "3px solid #ffffff", borderRadius: 12, fontWeight: 800, boxSizing: "border-box", resize: "vertical" },
    contactRow: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: 10,
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: isMobile ? "flex-start" : "space-between",
      flexWrap: isMobile ? "nowrap" : "wrap",
      marginTop: 10,
    },
    compactBtn: {
      minHeight: isMobile ? 44 : 34, padding: "6px 12px", borderWidth: 3, borderStyle: "solid",
      borderRadius: 8, fontWeight: 900, fontSize: 12, cursor: "pointer", textTransform: "uppercase",
      display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box",
    },
    textBtn: { background: "#008000", color: "#FFFFFF", borderColor: "#000000" },
    emailBtn: { background: "#0000FF", color: "#FFFFFF", borderColor: "#000000" },
    directionsBtn: { background: "#E8761D", color: "#000000", borderColor: "#000000" },
    printBtn: { background: "#000000", color: "#E8C98F", borderColor: "#E8761D" },
    finalActions: {
      display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
      marginTop: 12, paddingTop: 12, borderTop: "3px solid #E8E2DA",
    },
    saveBtn: {
      height: BTN_H, minWidth: BTN_MIN_W, padding: "0 14px", border: "3px solid #E8761D",
      borderRadius: 12, fontWeight: 900, background: "#000000", color: "#FFA500", cursor: "pointer",
    },
    cancelBtn: {
      height: BTN_H, minWidth: BTN_MIN_W, padding: "0 14px", border: "3px solid #000000",
      borderRadius: 12, fontWeight: 900, background: "#FFFFFF", color: "#000000", cursor: "pointer",
    },
    dangerBtn: { height: BTN_H, minWidth: BTN_MIN_W, width: isMobile ? "100%" : undefined, padding: "0 14px", border: "3px solid #ffffff", borderRadius: 12, fontWeight: 900, background: "#ff5252", color: "white", cursor: "pointer" },
  };

  // =========================
  // UI pieces
  // =========================
  const TitleBar = () => (
    <div style={styles.titleBarOuter}>
      <div style={styles.titleBarInner}>
        <div style={styles.titleLeft}>
          <div style={styles.titleIconBox}>📅</div>
          <div style={styles.titleTextWrap}>
            <div style={styles.titleMain}>SCHEDULER</div>
            <div style={styles.titleSub}>SUNDAY–SATURDAY • DAY / WEEK / MONTH</div>
          </div>
        </div>
        <div style={{
          display: isMobile && recurringAppointmentsEnabled ? "grid" : "flex",
          gridTemplateColumns: isMobile && recurringAppointmentsEnabled ? "repeat(2, auto)" : undefined,
          justifyContent: isMobile && recurringAppointmentsEnabled ? "center" : undefined,
          width: isMobile && recurringAppointmentsEnabled ? "100%" : undefined,
          gap: 10,
          flexWrap: "wrap",
        }}>
          <button type="button" style={{
            ...styles.titleActionBtn,
            gridColumn: isMobile && recurringAppointmentsEnabled ? "1 / -1" : undefined,
            justifySelf: isMobile && recurringAppointmentsEnabled ? "center" : undefined,
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#000000"; e.currentTarget.style.color = "#E8C98F"; e.currentTarget.style.borderColor = "#E8761D"; }}
            onClick={goToDashboard}>
            ⬅ BACK TO DASHBOARD
          </button>
          <button type="button" style={styles.titleActionBtn}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#000000"; e.currentTarget.style.color = "#E8C98F"; e.currentTarget.style.borderColor = "#E8761D"; }}
            onClick={() => setShowCalendarPrint(true)}>
            🖨 PRINT
          </button>
          <button type="button" style={styles.titleActionBtn}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#000000"; e.currentTarget.style.color = "#E8C98F"; e.currentTarget.style.borderColor = "#E8761D"; }}
            onClick={refreshAll}>
            🔄 REFRESH
          </button>
        </div>
      </div>
    </div>
  );

  const TopControls = () => (
    <div style={styles.controlsRow}>
      <div style={styles.leftGroup}>
        <button type="button" style={styles.btn(view === VIEW.DAY, true)}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
          onMouseLeave={(e) => (e.currentTarget.style.background = view === VIEW.DAY ? "#E8761D" : "white")}
          onClick={() => setView(VIEW.DAY)}>DAY</button>
        <button type="button" style={styles.btn(view === VIEW.WEEK, true)}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
          onMouseLeave={(e) => (e.currentTarget.style.background = view === VIEW.WEEK ? "#E8761D" : "white")}
          onClick={() => setView(VIEW.WEEK)}>WEEK</button>
        <button type="button" style={styles.btn(view === VIEW.MONTH, true)}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
          onMouseLeave={(e) => (e.currentTarget.style.background = view === VIEW.MONTH ? "#E8761D" : "white")}
          onClick={() => setView(VIEW.MONTH)}>MONTH</button>
      </div>
      <div style={{
        ...styles.centerGroup,
      }}>
        {recurringAppointmentsEnabled ? (
          <div style={styles.monthFrame}>{monthLabel}</div>
        ) : monthLabel}
      </div>
      <div style={styles.rightGroup}>
        <button type="button" style={styles.btn(false, true)}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
          onClick={() => {
            if (view === VIEW.DAY) setAnchorDate(addDays(anchorDate, -1));
            if (view === VIEW.WEEK) setAnchorDate(addDays(anchorDate, -7));
            if (view === VIEW.MONTH) setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 1, 1));
          }}>◀ PREV</button>
        <button type="button" style={styles.btn(false, true)}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
          onClick={() => setAnchorDate(new Date())}>TODAY</button>
        <button type="button" style={styles.btn(false, true)}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
          onClick={() => {
            if (view === VIEW.DAY) setAnchorDate(addDays(anchorDate, 1));
            if (view === VIEW.WEEK) setAnchorDate(addDays(anchorDate, 7));
            if (view === VIEW.MONTH) setAnchorDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 1));
          }}>NEXT ▶</button>
      </div>
    </div>
  );

  // =========================
  // Render
  // =========================
  return (
    <div className="jf-calendar-page" style={styles.pageWrap}>
      <SavedToastOverlay accentColor="#FFC107" />
      <style>{`
        @media (max-width: 767px) {
          .jf-calendar-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
        @media print {
          body { background: white !important; }
          body * { visibility: hidden !important; }
          .jf-calendar-print-root, .jf-calendar-print-root * { visibility: visible !important; }
          .jf-calendar-print-root { position: absolute !important; inset: 0 auto auto 0 !important; width: 100% !important; }
        }
      `}</style>

      <TitleBar />
      <TopControls />
      <CalendarSecondaryToolbar
        customers={customers}
        isMobile={isMobile}
        recurringEnabled={recurringAppointmentsEnabled}
        onOpenRecurring={() => setShowRecurringModal(true)}
        onOpenAppointment={openEdit}
        onGoToDate={openDayForDate}
      />

      <CalendarPrintDialog
        isOpen={showCalendarPrint}
        onClose={() => setShowCalendarPrint(false)}
        businessName={businessName}
      />

      {view === VIEW.DAY ? (
        <CalendarDayView anchorDate={anchorDate} apptsByDate={apptsByDate} todayYMD={todayYMD}
          openNewForDate={openNewForDate} openEdit={openEdit} fmtYMD={fmtYMD} fmtFriendly={fmtFriendly} styles={styles} />
      ) : null}
      {view === VIEW.WEEK ? (
        <CalendarWeekView isMobile={isMobile} weekDays={weekDays} apptsByDate={apptsByDate} todayYMD={todayYMD}
          openNewForDate={openNewForDate} openDayForDate={openDayForDate} openEdit={openEdit} fmtYMD={fmtYMD} fmtFriendly={fmtFriendly}
          weekStart={weekStart} styles={styles} />
      ) : null}
      {view === VIEW.MONTH ? (
        <CalendarMonthView isMobile={isMobile} monthGridDays={monthGridDays} apptsByDate={apptsByDate}
          todayYMD={todayYMD} anchorDate={anchorDate} monthLabel={monthLabel}
          openNewForDate={openNewForDate} openDayForDate={openDayForDate} openEdit={openEdit} fmtYMD={fmtYMD} styles={styles} />
      ) : null}

      {recurringAppointmentsEnabled ? (
        <RecurringAppointmentModal
          isOpen={showRecurringModal}
          onClose={() => setShowRecurringModal(false)}
          customers={customers}
          employees={employees}
          jobTypes={jobTypes}
          styles={styles}
          onCustomerCreated={loadCustomers}
          onCalendarRefresh={() => loadAppointmentsForRange(anchorDate, view)}
        />
      ) : null}

      {showModal ? (
        <AppointmentModal
          showModal={showModal}
          closeAppointmentModal={closeAppointmentModal}
          draft={draft}
          setDraftField={setDraftField}
          banner={banner}
          customers={customers}
          customerMobile={String(currentCustomer?.mobile_phone || "").trim()}
          customerEmail={String(currentCustomer?.billingEmail || currentCustomer?.email || "").trim()}
          employees={employees}
          jobTypes={jobTypes}
          bizHours={bizHours}
          useCustomerAddress={useCustomerAddress}
          setUseCustomerAddress={setUseCustomerAddress}
          applyCustomerAddressNow={applyCustomerAddressNow}
          customerLabel={customerLabel}
          showQuickAddCustomerUI={showQuickAddCustomerUI}
          setShowQuickAddCustomerUI={setShowQuickAddCustomerUI}
          quickCustomer={quickCustomer}
          setQC={setQC}
          saveQuickCustomer={saveQuickCustomer}
          loadCustomers={loadCustomers}
          saveAppointment={saveAppointment}
          deleteAppointment={deleteAppointment}
          deleteRecurringAppointment={deleteRecurringAppointment}
          deleteRecurringAppointments={deleteRecurringAppointments}
          recurringDeletionPending={recurringDeletionPending}
          textOnMyWayHref={onMyWayLinks.text}
          emailOnMyWayHref={onMyWayLinks.email}
          directionsHref={directionsHref}
          onPrint={() => {
            if (recurringDeletionInFlightRef.current) return;
            setShowModal(false);
            setShowPrintSheet(true);
          }}
          onViewFullSchedule={recurringAppointmentsEnabled ? () => setShowRecurringSeries(true) : undefined}
          styles={styles}
        />
      ) : null}

      <RecurringSeriesSchedule
        isOpen={showRecurringSeries}
        appointmentId={draft.id}
        currentAppointmentId={draft.id}
        onClose={() => setShowRecurringSeries(false)}
        onOpenAppointment={(appointment) => {
          setShowRecurringSeries(false);
          openEdit(appointment);
        }}
      />

      {showPrintSheet ? (
        <AppointmentPrintSheet
          appointment={{
            ...draft,
            start_time: draft.date && draft.start_time ? `${draft.date}T${draft.start_time}` : null,
            end_time: (draft.end_date || draft.date) && draft.end_time ? `${draft.end_date || draft.date}T${draft.end_time}` : null,
          }}
          customer={currentCustomer || { name: draft.customer_name, phone: draft.notes ? (draft.notes.match(/Phone:\s*([^\n]+)/) || [])[1] || '' : '', address: draft.service_street, city: draft.service_city, state: draft.service_state, zip: draft.service_zip }}
          employee={currentEmployee || { name: draft.employee_name }}
          jobType={currentJobType?.name || draft.job_type_name || draft.title}
          businessName={businessName}
          onClose={() => { setShowPrintSheet(false); setShowModal(true); }}
        />
      ) : null}
    </div>
  );
};

export default CalendarPage;
